import {isPlatformBrowser, NgOptimizedImage, NgTemplateOutlet} from "@angular/common";
import type {WritableSignal} from "@angular/core";
import {Component, computed, inject, PLATFORM_ID, type Signal, signal} from "@angular/core";
import type {IsActiveMatchOptions} from "@angular/router";
import {Router, RouterLink, RouterLinkActive} from "@angular/router";
import {NgbCollapse, NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import type {BootstrapIconsId} from "bootstrap-icons";
import {of} from "rxjs";
import type {NonEmptyTuple, RequireAtLeastOne, RequireExactlyOne, RequireOneOrNone} from "type-fest";
import type {StoredCredentials} from "../../../common/credential-storage";
import {type AppRoute} from "../../app.routes";
import {BootstrapIcon} from "../../directives/bootstrap-icon";
import {RouterLinkAppRoute} from "../../directives/router-link-app-route";
import {TypedTemplateContext} from "../../directives/typed-template-context";
import {changeLanguageIfNeeded} from "../../helpers/LanguageChanges";
import {rxSafeResource} from "../../helpers/ResourceHelpers";
import {completionOf} from "../../helpers/RxJsOperators";
import type {Optional} from "../../helpers/TypeHelpers";
import type {Existing} from "../../models/base/IdentifiableModel";
import {LANGUAGE_CODE, type LanguageCode} from "../../models/i18n/Language";
import {PermissionId, type Permissions} from "../../models/users/Permission";
import {type ContactableUser, routeComponentFromUserType, UserAccountType} from "../../models/users/User";
import {AuthApi} from "../../services/api/auth-api";
import {I18nApi} from "../../services/api/i18n-api";
import {UsersApi} from "../../services/api/users/users-api";
import {CredentialsStorage} from "../../services/credentials-storage";
import {Toasts} from "../../services/toasts";
import {FlagIcon} from "../flag-icon/flag-icon";
import {ButtonLoadingIndicator} from "../loading-indicator/loading-indicator";

type MenuItemLabel = RequireAtLeastOne<{
  readonly icon?: BootstrapIconsId;
  readonly title?: string;
}> & {
  readonly tooltip?: string;
};

interface MenuItemAction {
  readonly isBeingHandled: WritableSignal<boolean>;
  readonly isDestructive: boolean;
  readonly handler: () => Promise<void>;
}

type MenuItemActionOrRoute = RequireExactlyOne<{
  readonly route?: AppRoute;
  readonly action?: MenuItemAction;
}>;

interface _MenuItemChildren {
  readonly children: readonly MenuItem[];
}

type MenuItemTarget = RequireOneOrNone<MenuItemActionOrRoute & _MenuItemChildren, keyof MenuItemActionOrRoute>;
// (MenuItemActionOrRoute & _MenuItemChildren) | _MenuItemChildren;
type MenuItem = { readonly id: string; } & MenuItemLabel & MenuItemTarget;

@Component({
  selector: "rf-navbar",
  templateUrl: "./navbar.html",
  styleUrl: "./navbar.scss",
  imports: [
    RouterLink,
    RouterLinkAppRoute,
    NgOptimizedImage,
    RouterLinkActive,
    TypedTemplateContext,
    NgTemplateOutlet,
    BootstrapIcon,
    NgbTooltip,
    NgbCollapse,
    ButtonLoadingIndicator,
    FlagIcon,
  ],
})
export class Navbar {
  protected readonly MenuItemCtx: { readonly $implicit: Readonly<MenuItem> };

  protected readonly routerLinkActiveOptions: Readonly<IsActiveMatchOptions> = {
    paths: "exact",
    queryParams: "subset",
    fragment: "ignored",
    matrixParams: "ignored",
  };

  protected readonly currentLanguageCode = inject(LANGUAGE_CODE);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly i18nApi = inject(I18nApi);
  private readonly usersApi = inject(UsersApi);
  private readonly credentialsStorage = inject(CredentialsStorage);
  private readonly authApi = inject(AuthApi);
  private readonly toasts = inject(Toasts);
  private readonly router = inject(Router);

  private get credentials(): Signal<Optional<StoredCredentials>> {
    return this.credentialsStorage.nonExpiredCredentials;
  }

  protected readonly currentUser = rxSafeResource({
    params: () => this.credentials()?.isExpired === false,
    stream: ({params: shouldLoad}) =>
      shouldLoad ? this.usersApi.getContactableMe() : of(undefined),
  });

  protected readonly languages = rxSafeResource({
    stream: () => this.i18nApi.languageSelectOptions(true),
    defaultValue: [],
  });

  protected readonly navbarCollapsed = signal(true);

  protected readonly menuItems = computed(() =>
    Navbar.createMenu(this.credentials(),
      this.credentialsStorage.permissions(),
      this.currentUser.valueOrDefault(),
      this.authApi,
      this.toasts,
      this.router));

  protected async changeLanguage(newLanguage: LanguageCode): Promise<void> {
    // Not checking isValid since that would prevent changing language for users that require password change.
    // Also, we're not disabling the control, because if there's no user, we simply do not persist the choice.
    if (this.credentials()?.isExpired === false)
      await completionOf(this.usersApi.changeMyLanguage(newLanguage));
    if (isPlatformBrowser(this.platformId))
      changeLanguageIfNeeded(this.currentLanguageCode, newLanguage, window);
  }

  protected toggleNavbar(): void {
    this.navbarCollapsed.update(nc => !nc);
  }

  protected closeNavbar(): void {
    this.navbarCollapsed.set(true);
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  protected async handleAction(action: Readonly<MenuItemAction>): Promise<void> {
    action.isBeingHandled.set(true);
    try {
      await action.handler();
    } finally {
      action.isBeingHandled.set(false);
      this.closeNavbar();
    }
  }

  private static createMenu(credentials: Readonly<Optional<StoredCredentials>>,
                            permissions: Readonly<Permissions>,
                            currentUser: Readonly<Optional<Existing<ContactableUser>>>,
                            // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
                            authService: AuthApi,
                            // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
                            toastService: Toasts,
                            router: Router): readonly Readonly<MenuItem>[] {
    if (!credentials) {
      return [
        {id: "home", title: $localize`Home`, route: "/home", children: []},
        {id: "login", title: $localize`Login`, route: "/login", children: []},
      ];
    }

    const menuBuilder = new MenuBuilder();
    if (credentials.isValid) {
      menuBuilder.addRouteItem("int-dashboard", $localize`Dashboard`, "/dashboard")
        .ifUserIs(UserAccountType.Admin, UserAccountType.Bactoforce);
      menuBuilder.addRouteItem("ext-dashboard", $localize`Home`, "/dashboard")
        .ifUserIs(UserAccountType.Customer, UserAccountType.ThirdParty);

      menuBuilder.addParentItem("master-data", $localize`Master Data`, b => {
        b.addRouteItem("bactoforce-users", $localize`BF Users`, "/users/bactoforce");
        b.addRouteItem("customer-users", $localize`Customers`, "/users/customers");
        b.addRouteItem("customer-organizations", $localize`Organizations`, "/organizations");
        b.addRouteItem("customer-locations", $localize`Locations`, "/locations");
        b.addRouteItem("equipment", $localize`:@@equipment.plural:Equipment`, "/equipment");
        b.addRouteItem("instruments", $localize`Instruments`, "/instruments");
        b.addRouteItem("materials", $localize`Materials`, "/materials");
        b.addRouteItem("price-lists", $localize`Price Lists`, "/price-lists");
        b.addRouteItem("datasheets", $localize`Datasheets`, "/datasheets");
        b.addRouteItem("budget-numbers", $localize`Budget Numbers`, "/budget-numbers")
          .ifPermitted(PermissionId.ManageBudgetNumbers);
        b.addRouteItem("third-party-companies", $localize`TP Companies`, "/third-party-companies")
          .ifPermitted(PermissionId.ManageThirdPartyCompany);
        b.addRouteItem("third-party-users", $localize`TP Users`, "/users/third-party")
          .ifPermitted(PermissionId.ManageThirdPartyUsers);
        b.addRouteItem("inspection-type-extract", $localize`Inspection Type Extract`, "/equipment/inspection-types");
        b.addRouteItem("equipment-inspection-type-update", $localize`Equipment Update`, "/equipment/inspection-types/update")
          .ifPermitted(PermissionId.BulkUpdateEquipmentInspectionTypes);
        b.addRouteItem("customer-database", $localize`Customer Database`, "/customer-database")
          .ifPermitted(PermissionId.AccessCustomerDatabase);
      }).ifUserIs(UserAccountType.Admin, UserAccountType.Bactoforce);

      menuBuilder.addRouteItem("inspection-overview", $localize`Inspection Overview`, "/inspections/overview")
        .ifUserIs(UserAccountType.Customer, UserAccountType.ThirdParty);

      menuBuilder.addRouteItem("reports", $localize`Reports`, "/inspections/reports");

      menuBuilder.addRouteItem("visits", $localize`Visits`, "/visits")
        .ifUserIs(UserAccountType.Customer, UserAccountType.ThirdParty);

      menuBuilder.addRouteItem("datasheets", $localize`Datasheets`, "/datasheets")
        .ifUserIs(UserAccountType.Customer);

      menuBuilder.addParentItem("scheduling", $localize`Scheduling`, b => {
        b.addRouteItem("inspection-overview", $localize`Inspection Overview`, "/inspections/overview");
        b.addRouteItem("inspection-planner", $localize`Inspection Planner`, "/inspections/planner");
        b.addRouteItem("visits", $localize`Visits`, "/visits");
        b.addRouteItem("calendar", $localize`Calendar`, "/calendar");
        b.addRouteItem("time-entries", $localize`Other Times`, "/time-entries");
      }).ifUserIs(UserAccountType.Admin, UserAccountType.Bactoforce);

      menuBuilder.addParentItem("risk-management", $localize`:@@navigation.group.risk-management.title:Risk Management`, b => {
        b.addRouteItem("dashboard", $localize`:@@navigation.page.risk-management.dashboard.title:Dashboard`, "/risk-management/dashboard");
      }).ifPermitted(PermissionId.SeeRiskManagementFeatures);

      menuBuilder.addParentItem("enterprise", $localize`Enterprise`, b => {
        b.addRouteItem("forecast", $localize`Forecast`, "/enterprise/forecast");
        b.addRouteItem("forecast-summary", $localize`Forecast Summary`, "/enterprise/forecast/summary")
          .ifPermitted(PermissionId.SeeForecastSummary);
        b.addRouteItem("year-to-date", $localize`Year to Date (YtD)`, "/enterprise/year-to-date");
        b.addRouteItem("year-to-date-summary", $localize`Year to Date Report Total`, "/enterprise/year-to-date/report-total")
          .ifPermitted(PermissionId.SeeYearToDateSummary);
        b.addRouteItem("planned", $localize`Planned / Plannable`, "/enterprise/planned");
        b.addRouteItem("sales-log", $localize`Sales Log Activity Log`, "/sales-log")
          .ifPermitted(PermissionId.SeeSalesActivityLog);
      }).ifPermitted(PermissionId.SeeEnterpriseFeatures);

      menuBuilder.addParentItem("system", $localize`System`, b => {
        b.addRouteItem("admin-users", $localize`Admins`, "/users/admins")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("roles", $localize`Roles`, "/roles")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("special-rights", $localize`Special Rights`, "/permissions")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("bactoforce-areas", $localize`Bactoforce Areas`, "/bactoforce-areas")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("bactoforce-locations", $localize`Bactoforce Locations`, "/bactoforce-locations")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("articles", $localize`Articles`, "/articles")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("industry-sections", $localize`Industry Sections`, "/industry-sections")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("customer-organization-merge", $localize`Merge Organizations`, "/organizations/merge")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("price-lists-price-lists", $localize`Bulk Update Price Lists`, "/price-lists/bulk-update/prices")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("price-lists-special-prices", $localize`Bulk Update Special Prices`, "/price-lists/bulk-update/special-prices")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("budget-numbers", $localize`Bulk Import Budget Numbers`, "/budget-numbers/bulk-import")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("currencies", $localize`Currencies`, "/currencies")
          .ifPermitted(PermissionId.ManageCurrencies);
        b.addRouteItem("languages", $localize`Languages`, "/i18n/languages")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("countries", $localize`Countries`, "/i18n/countries")
          .ifUserIs(UserAccountType.Admin);
        b.addRouteItem("sql-export", $localize`SQL Export`, "/system/sql-export")
          .ifUserIs(UserAccountType.Admin);
      });
    }

    menuBuilder.addParentItem("profile", {icon: "person-fill", tooltip: currentUser?.fullName}, b => {
      if (!credentials.isExpired) {
        if (!credentials.needsPasswordChange)
          b.addRouteItem("update-me", $localize`Profile`, ["/users", routeComponentFromUserType(credentials.accountType), credentials.userId])
            .ifUserIs(UserAccountType.Admin, UserAccountType.Bactoforce);
        b.addRouteItem("change-password", $localize`Change Password`, "/change-password");
      }
      b.addDestructiveActionItem("logout", {title: $localize`Logout`, icon: "box-arrow-left"}, async () => {
        // First navigate home, then log out to avoid any issues when the credentials signal triggers
        // and thus potentially reloads data on the current page.
        await router.navigate(["/home"] satisfies AppRoute);
        toastService.clear();
        await completionOf(authService.logout());
      });
    });

    return menuBuilder.build(credentials.accountType, permissions);
  }
}

class MenuBuilder {
  protected readonly items: MenuItemBuilder[] = [];

  private addItem(id: string, label: string | Readonly<MenuItemLabel>): MenuItemBuilder {
    const builder = new MenuItemBuilder(id, typeof label === "string" ? {title: label} : label);
    this.items.push(builder);
    return builder;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public addRouteItem(id: string, label: string | Readonly<MenuItemLabel>, route: AppRoute): MenuItemBuilder {
    return this.addItem(id, label).withRoute(route);
  }

  public addPossiblyDestructiveActionItem(id: string, label: string | Readonly<MenuItemLabel>, isDestructive: boolean, action: () => Promise<void>): MenuItemBuilder {
    return this.addItem(id, label).withPossiblyDestructiveAction(isDestructive, action);
  }

  public addActionItem(id: string, label: string | Readonly<MenuItemLabel>, action: () => Promise<void>): MenuItemBuilder {
    return this.addPossiblyDestructiveActionItem(id, label, false, action);
  }

  public addDestructiveActionItem(id: string, label: string | Readonly<MenuItemLabel>, action: () => Promise<void>): MenuItemBuilder {
    return this.addPossiblyDestructiveActionItem(id, label, true, action);
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public addParentItem(id: string, labelOrIcon: string | Readonly<MenuItemLabel>, childrenBuilder: (builder: MenuBuilder) => void): MenuItemBuilder {
    return this.addItem(id, typeof labelOrIcon === "string" ? {title: labelOrIcon} : labelOrIcon).addChildren(childrenBuilder);
  }

  protected _build(userType: UserAccountType, permissions: Readonly<Permissions>, parentId?: string): readonly MenuItem[] {
    return this.items.map(i => i.build(userType, permissions, parentId)).filter(i => i !== null);
  }

  public build(userType: UserAccountType, permissions: Readonly<Permissions>): readonly MenuItem[] {
    return this._build(userType, permissions);
  }
}

class ChildMenuBuilder extends MenuBuilder {
  public buildForParent(userType: UserAccountType, permissions: Readonly<Permissions>, parentId: string): readonly MenuItem[] {
    return this._build(userType, permissions, parentId);
  }
}

class MenuItemBuilder {
  private actionOrRoute?: MenuItemActionOrRoute;
  private readonly children: ChildMenuBuilder[] = [];
  private requiredUserTypes?: NonEmptyTuple<UserAccountType>;
  private requiredPermissions?: NonEmptyTuple<PermissionId>;

  public constructor(private readonly id: string,
                     private readonly label: Readonly<MenuItemLabel>) {
  }

  public ifUserIs(...userTypes: NonEmptyTuple<UserAccountType>): this {
    this.requiredUserTypes = userTypes;
    return this;
  }

  public ifPermitted(...permissions: NonEmptyTuple<PermissionId>): this {
    this.requiredPermissions = permissions;
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public withRoute(route: AppRoute): this {
    this.actionOrRoute = {route};
    return this;
  }

  public withPossiblyDestructiveAction(isDestructive: boolean, action: () => Promise<void>): this {
    this.actionOrRoute = {action: {isDestructive, handler: action, isBeingHandled: signal(false)}};
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public addChildren(childBuilder: (builder: MenuBuilder) => void): this {
    const builder = new ChildMenuBuilder();
    this.children.push(builder);
    childBuilder(builder);
    return this;
  }

  public build(userType: UserAccountType, permissions: Readonly<Permissions>, parentId?: string): Readonly<MenuItem> | null {
    if (this.requiredUserTypes && !this.requiredUserTypes.includes(userType)) return null;
    if (this.requiredPermissions && !permissions.hasPermissions(...this.requiredPermissions)) return null;
    const ourId = parentId !== undefined ? `${parentId}-${this.id}` : this.id;
    const children = this.children.flatMap(c => c.buildForParent(userType, permissions, ourId));
    if (!this.actionOrRoute && children.length <= 0) return null;
    return {
      id: ourId,
      ...this.label,
      ...this.actionOrRoute,
      children,
    };
  }
}
